import { redirect } from '@tanstack/react-router';

import { canAccess, hasAnyPermission, type Action, type Resource } from './permissions';
import { useSesionStore } from './session.store';
import {
  esAuxiliar,
  esParticipantePrivado,
  esSupervisor,
  type SessionUser,
} from './usuario.entity';

/**
 * Ruta de inicio según el rol. El Dashboard es solo para roles internos con
 * alcance de portafolio (Admin, Super Admin); los actores externos
 * (participante privado) y el Auxiliar de campo inician en Incidencias, y el
 * Supervisor en Proyectos (su alcance operativo). Es una heurística de UX
 * (dónde aterrizar), no un candado de autorización. Se usa como destino del
 * rebote desde `/dashboard` y como destino de los guards de acceso denegado.
 */
export function rutaInicialDe(user: Pick<SessionUser, 'rol'> | null | undefined): string {
  if (esParticipantePrivado(user)) return '/incidencias';
  if (esAuxiliar(user)) return '/incidencias';
  if (esSupervisor(user)) return '/proyectos';
  return '/dashboard';
}

/**
 * Guard para `beforeLoad`: redirige a la ruta de inicio del usuario si no
 * tiene el permiso del módulo. Solo por PERMISO (ADR-038: el permiso manda)
 * — sin vetos por rol, para que un permiso delegado abra la ruta sin tocar
 * código. El backend sigue siendo la fuente de verdad (responde 403).
 */
export function exigirAcceso(resource: Resource, action: Action = 'read'): void {
  const user = useSesionStore.getState().user;
  if (!canAccess(user, resource, action)) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- patrón de redirect de TanStack Router
    throw redirect({ to: rutaInicialDe(user) });
  }
}

/**
 * Variante por permisos literales (any-of), para rutas cuya visibilidad no
 * mapea a un verbo CRUD del switch de `permisosRequeridos` (p. ej. una
 * capability puntual).
 */
export function exigirAlgunPermiso(...permisos: string[]): void {
  const user = useSesionStore.getState().user;
  if (!hasAnyPermission(user, ...permisos)) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- patrón de redirect de TanStack Router
    throw redirect({ to: rutaInicialDe(user) });
  }
}

/**
 * Guard de `/mapa`, superficie exclusiva del **Auxiliar de campo**: el resto
 * de roles ya tiene el mismo visor dentro del detalle de proyecto, así que se
 * les rebota a su inicio (misma excepción de UX por rol que el guard de
 * `/dashboard` para el PP — la autorización real sigue siendo por permiso).
 * Además del rol se exige `proyectos:listar`, el permiso que piden los
 * endpoints geo de lectura (trazado KMZ, capa de incidencias y descarga).
 */
export function exigirMapaAuxiliar(): void {
  const user = useSesionStore.getState().user;
  if (!esAuxiliar(user) || !canAccess(user, 'proyectos', 'read')) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- patrón de redirect de TanStack Router
    throw redirect({ to: rutaInicialDe(user) });
  }
}
